import {
  addGarages,
  createGarageAdditionalSettings,
  deleteGarageAdditionalSettings,
  getGarageAdditionalSettings,
  getGarageDetail,
  updateGarageAdditionalSettings,
  updateGarages,
} from 'features/garages/actions';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import { ReactComponent as GarageFilledIcon } from 'icons/garage-filled-icon.svg';
import { ReactComponent as MagnifierIcon } from 'icons/magnifier.svg';
import { ReactComponent as PlusIcon } from 'icons/plus-icon.svg';
import { ReactComponent as DeleteIcon } from 'icons/delete-rounded-icon.svg';
import { Button, InputField, TableWrapper } from 'components/Global';
import { useAppContext } from 'components/Context/AppContext';
import SelectFieldTime from 'components/Global/SelectFieldTime';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import { resetGarageDetail } from 'features/garages/slice';
import { checkPermission, toPascalCase } from 'utils/functionality';
import AdditionalGarageSetting from '../AdditionalGarageSetting';
import { getAllRentalLocation } from 'features/rental-location/actions';
import { getServicesByRentalLocation } from 'features/services/actions';
import { trackEvent } from 'utils/analytics';

const GarageDetailComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { showToast, setShowConfirmation } = useAppContext();

  const { data } = useSelector((state) => state.rentalLocation);
  const { garageDetail, garageAdditionalSetting } = useSelector((state) => state.garages);
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: rentalServicesData } = useSelector((state) => state.services);

  const [mainSettingValue, setMainSettingValue] = useState({
    id: 0,
    name: '',
    location_id: 0,
    address_details: '',
    map_link: '',
  });
  const [additionalSettingValue, setAdditionalSettingValue] = useState([
    {
      id: 0,
      garage_id: 0,
      fee: '',
      status: false,
      service: '',
      vehicle_ids: [],
      vehicles: [],
      category_ids: [],
      categories: [],
    },
  ]);
  const [additionalSettingToUpdate, setAdditionalSettingToUpdate] = useState([]);
  const [mappedRentTypes, setMappedRentTypes] = useState([]);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');

  useEffect(() => {
    if (!id) return;
    dispatch(getGarageDetail(id));
    dispatch(getGarageAdditionalSettings(id));
  }, [id]);

  useEffect(() => {
    dispatch(getAllRentalLocation());
  }, []);

  useEffect(() => {
    if (!mainSettingValue.location_id) return;
    dispatch(getServicesByRentalLocation(mainSettingValue.location_id));
  }, [mainSettingValue.location_id]);

  useEffect(() => {
    if (!isPageLoaded) return;
    if (!rentalServicesData.length) return;
    setMappedRentTypes(
      rentalServicesData?.[0]?.facilities
        ?.filter?.(
          (item) => item.name === 'With Driver' || item.name === 'Without Driver' || item.name === 'Airport Transfer',
        )
        ?.map((item) => ({
          id: item.id,
          name: item.name,
          service: item.name?.toLowerCase()?.split(' ')?.join('_'),
          value: item.name?.toLowerCase()?.split(' ')?.join('_'),
          outside_operational_status: false,
          outside_operational_fee: '',
          start_time: '',
          end_time: '',
        })),
    );
  }, [rentalServicesData, isPageLoaded]);

  useEffect(() => {
    if (Object.keys(garageDetail).length === 0) return;

    setMainSettingValue({
      ...mainSettingValue,
      name: garageDetail.name,
      location_id: garageDetail.location_id,
      map_link: garageDetail.map_link,
      address_details: garageDetail.address_details,
    });
    setMappedRentTypes(
      garageDetail.operational
        ?.filter?.(
          (item) =>
            toPascalCase(item.service) === 'With Driver' ||
            toPascalCase(item.service) === 'Without Driver' ||
            toPascalCase(item.service) === 'Airport Transfer',
        )
        ?.map((item, idx) => ({
          id: idx + 1,
          name: toPascalCase(item.service),
          service: item.service,
          value: item.service,
          outside_operational_status: item.outside_operational_status,
          outside_operational_fee: item.outside_operational_fee,
          start_time: item.start_time,
          end_time: item.end_time,
        })) ?? [],
    );
  }, [garageDetail]);

  useEffect(() => {
    if (garageAdditionalSetting === null || !mappedRentTypes?.length) return;

    setAdditionalSettingToUpdate(
      garageAdditionalSetting.map((item) => {
        let newSelectedCar = [];

        for (let vehicle of item.vehicles) {
          if (newSelectedCar.length === 0) {
            newSelectedCar.push({ cars: [{ ...vehicle, checked: true }], total: 1 });
          } else {
            const existingVehicle = newSelectedCar.find((item) => item.cars[0].name === vehicle.name);
            if (existingVehicle) {
              const copyNewSelectedCar = [...newSelectedCar];
              copyNewSelectedCar[copyNewSelectedCar.indexOf(existingVehicle)] = {
                cars: [...existingVehicle.cars, { ...vehicle, checked: true }],
                total: existingVehicle.total + 1,
              };
              newSelectedCar = copyNewSelectedCar;
            } else {
              newSelectedCar = [...newSelectedCar, { cars: [{ ...vehicle, checked: true }], total: 1 }];
            }
          }
        }

        return {
          ...item,
          vehicle_ids: newSelectedCar,
          category_ids: item.categories.map((item) => ({ ...item, value: item.name.toLowerCase() })),
          service: mappedRentTypes.find((type) => type.value == item.service)?.id,
        };
      }) || [],
    );
  }, [garageAdditionalSetting, mappedRentTypes]);

  const sendPayloadData = async (action, payload, fulfilledMessage, errorMessage1, withAdditionalSetting) => {
    try {
      trackEvent(action === 'ADD' ? 'garage_created' : 'garage_updated', {
        garage_id: action === 'ADD' ? undefined : garageDetail?.id,
        source: 'garage_detail',
      });
      let res;
      if (action === 'ADD') {
        res = await dispatch(addGarages(payload)).unwrap();
      } else if (action === 'UPDATE') {
        await dispatch(updateGarages({ id: garageDetail.id, payload })).unwrap();
      }

      if (withAdditionalSetting) {
        additionalSettingToUpdate.map(async (item) => {
          await dispatch(
            updateGarageAdditionalSettings({
              payload: {
                ...item,
                fee: +item.fee,
                category_ids: item.category_ids.map((item) => item.id),
                vehicle_ids: item.vehicle_ids
                  .map((item) => item.cars)
                  .map((cars) => cars.map((car) => car.id))
                  .flat(),
                service: mappedRentTypes.find((type) => type.id == item.service)?.value,
              },
              id: item.id,
            }),
          ).unwrap();
        });
        additionalSettingValue.map(async (item) => {
          if (!item.service) return;
          await dispatch(
            createGarageAdditionalSettings({
              ...item,
              garage_id: action === 'ADD' ? res.id : garageDetail.id,
              fee: +item.fee,
              category_ids: item.category_ids.map((item) => item.id),
              vehicle_ids: item.vehicle_ids
                .map((item) => item.cars)
                .map((cars) => cars.map((car) => car.id))
                .flat(),
              service: mappedRentTypes.find((type) => type.id == item.service)?.value,
            }),
          ).unwrap();
        });
      }
      showToast({ type: 'success', message: fulfilledMessage });
      navigate(-1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      showToast({ type: 'error', message: `${errorMessage1}: ${error}` });
    }
  };

  const handleKeyDown = (event) => {
    // Prevent the input of commas, dots, and other non-numeric characters
    if (event.key === ',' || event.key === '.' || event.key === 'e' || event.key === '-') {
      event.preventDefault();
    }
  };

  const handleValueInput = (e, typeId) => {
    setMappedRentTypes((prev) =>
      prev.map((item) => (item.id == typeId ? { ...item, outside_operational_fee: +e.target.value } : item)),
    );
  };

  const handleAddGarage = async () => {
    const payload = {
      name: mainSettingValue.name,
      location_id: parseInt(mainSettingValue.location_id),
      address_details: mainSettingValue.address_details,
      map_link: mainSettingValue.map_link,
      operational: mappedRentTypes.map((item) => ({
        ...item,
        outside_operational_fee: item.outside_operational_fee || 0,
      })),
    };

    // validation
    const validName = mainSettingValue.name.replace(/\s/).length !== 0;
    const validLocation = mainSettingValue.location_id !== 0;
    const validAddressDetails = mainSettingValue.address_details.replace(/\s/).length !== 0;
    const validMapLink = mainSettingValue.map_link.replace(/\s/).length !== 0;
    let isWithAddtionalSetting = false;

    // check validation
    if (!validName) {
      return showToast({ type: 'error', message: 'Masukan Nama Garasi' });
    } else if (!validLocation) {
      return showToast({ type: 'error', message: 'Pilih Lokasi Rental' });
    } else if (!validMapLink) {
      return showToast({ type: 'error', message: 'Masukan Link Gmaps' });
    } else if (!validAddressDetails) {
      return showToast({ type: 'error', message: 'Masukan Alamat Garasi' });
    }

    // check additional settings validation
    if (additionalSettingValue.length > 0) {
      for (let additionalChild of additionalSettingValue) {
        if (additionalChild.service) {
          isWithAddtionalSetting = true;
          if (additionalChild.fee === '') {
            return showToast({ type: 'error', message: 'Masukkan Tarif Penalti Setting' });
          }
        }
      }
    }

    if (additionalSettingToUpdate.length > 0) {
      for (let additionalChild of additionalSettingToUpdate) {
        if (additionalChild.service) {
          isWithAddtionalSetting = true;
          if (additionalChild.fee === '') {
            return showToast({ type: 'error', message: 'Masukkan Tarif Penalti Setting' });
          }
        }
      }
    }

    // if add garage
    if (Object.keys(garageDetail).length === 0) {
      sendPayloadData('ADD', payload, 'Berhasil Tambah Garasi', 'Gagal Tambah Garasi', isWithAddtionalSetting);
      return;
    }

    // if update garage
    sendPayloadData('UPDATE', payload, 'Berhasil Update Garasi', 'Gagal Update Garasi', isWithAddtionalSetting);
    dispatch(resetGarageDetail());
  };

  const handlePenaltiStatus = (id) => {
    setMappedRentTypes((prev) =>
      prev.map((item) =>
        item.id == id ? { ...item, outside_operational_status: !item.outside_operational_status } : item,
      ),
    );
  };

  const handleLocation = (e) => {
    setMainSettingValue((prev) => ({ ...prev, location_id: e.target.id }));
    setIsPageLoaded(true);
  };

  const numberInputOnWheelPreventChange = (e) => {
    e.target.blur();
    e.stopPropagation();

    setTimeout(() => {
      e.target.focus();
    }, 0);
  };

  const handleAddGarageSetting = () => {
    setAdditionalSettingValue((prev) => [
      ...prev,
      {
        id: 0,
        garage_id: 0,
        fee: '',
        status: false,
        service: '',
        vehicle_ids: [],
        vehicles: [],
        category_ids: [],
        categories: [],
      },
    ]);
  };

  const handleRemoveGarageSetting = async (index, settingId) => {
    if (garageAdditionalSetting?.length > 0 && settingId) {
      setShowConfirmation({
        message: 'Apakah anda yakin ingin menghapus Garasi',
        show: true,
        onClick: async () => {
          try {
            await dispatch(deleteGarageAdditionalSettings(settingId)).unwrap();
            showToast({ type: 'success', message: 'Berhasil Menghapus Pengaturan Garasi' });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            showToast({ type: 'error', message: 'Gagal Menghapus Pengaturan Garasi' });
          } finally {
            dispatch(getGarageAdditionalSettings(id));
          }
        },
      });
      return;
    }
    setAdditionalSettingValue((prev) => prev.filter((_, idx) => idx !== index));
  };

  const selectedCountry = data?.find((country) => country.id === +mainSettingValue.location_id);
  const isSingaporeSelected = selectedCountry?.name === 'Singapore';
  const isInDetailPage = location.pathname === '/garages/detail';

  return (
    <div className="garage-detail">
      <header className="detail-setting-harga__header" onClick={() => navigate(-1)}>
        <LeftArrow />
        <p>Kembali</p>
      </header>

      <TableWrapper
        title={id ? 'Detail Garasi' : 'Tambah Garasi'}
        icon={<GarageFilledIcon fill="#009EF7" width="25px" height="25px" />}
      >
        <div className="garage-detail__main-setting">
          <h2 className="garage-detail__main-header">Pengaturan Utama</h2>
          <div className="garage-detail__main-setting__form">
            <InputField
              className={'garage-input'}
              label={'Nama Garasi'}
              placeholder={'Masukkan Nama Garasi'}
              value={mainSettingValue.name}
              onChange={(e) => setMainSettingValue((prev) => ({ ...prev, name: e.target.value }))}
              id="nama-garasi"
              name={'nama-garasi'}
            />

            <InputField
              className={'garage-input'}
              label={'Link Gmaps'}
              placeholder={'Masukkan Link'}
              value={mainSettingValue.map_link}
              onChange={(e) => setMainSettingValue((prev) => ({ ...prev, map_link: e.target.value }))}
              id="garasi-map-link"
              name={'map_link'}
            />

            <InputField
              className={'garage-input'}
              label={'Alamat Garasi'}
              placeholder={'Masukkan Alamat Garasi'}
              value={mainSettingValue.address_details}
              onChange={(e) => setMainSettingValue((prev) => ({ ...prev, address_details: e.target.value }))}
              icon={<MagnifierIcon />}
            />

            <SelectFieldDropdown
              label="Lokasi Rental"
              type="select"
              htmlFor="garage-city"
              data={data}
              value={mainSettingValue.location_id}
              placeholder={'Pilih Lokasi'}
              onChange={handleLocation}
            />

            {isInDetailPage && isSingaporeSelected ? (
              <div className="garage-detail__exchange-rate">
                <p> Nilai Mata Uang:</p>
                <p className="rate">1 SGD = 1242</p>
              </div>
            ) : null}
          </div>

          {mappedRentTypes?.length > 0
            ? mappedRentTypes.map((item, idx) => (
                <div key={`types-${idx}`} className="garage-detail__main-setting setting-types">
                  <h2 className="garage-detail__main-header">{item.name}</h2>
                  <div className="garage-detail__main-setting__form">
                    <InputField
                      label="Tarif Penalti"
                      type="number"
                      placeholder="Masukkan Jumlah Tarif Penalti"
                      value={item.outside_operational_fee || ''}
                      onChange={(e) => handleValueInput(e, item.id)}
                      onWheel={numberInputOnWheelPreventChange}
                      min={0}
                      onKeyDown={handleKeyDown}
                    />

                    <div className="garage-detail__time">
                      <SelectFieldTime
                        label="Jam Buka"
                        htmlFor="jam-buka"
                        placeholder="00:00"
                        value={item.start_time}
                        onChange={(hour, minute) => {
                          setMappedRentTypes((prev) =>
                            prev.map((type) =>
                              type.id == item.id
                                ? { ...type, start_time: `${hour ? hour : '00'}:${minute ? minute : '00'}` }
                                : type,
                            ),
                          );
                        }}
                        icon={false}
                        showAllHours={true}
                      />
                      <SelectFieldTime
                        label="Jam Tutup"
                        htmlFor="jam-tutup"
                        placeholder="00:00"
                        value={item.end_time}
                        onChange={(hour, minute) => {
                          setMappedRentTypes((prev) =>
                            prev.map((type) =>
                              type.id == item.id
                                ? { ...type, end_time: `${hour ? hour : '00'}:${minute ? minute : '00'}` }
                                : type,
                            ),
                          );
                        }}
                        disable={item.start_time === '' ? true : false}
                        icon={false}
                        showAllHours={true}
                      />
                    </div>

                    <div className="garage-detail__operational-status">
                      <p>Penalti Status</p>

                      <div className="garage-detail__status-info">
                        <div className="user__toggle-status">
                          <label className="switch">
                            <input
                              type="checkbox"
                              onChange={() => handlePenaltiStatus(item.id)}
                              role={'switch'}
                              checked={item.outside_operational_status}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>

                        {item.outside_operational_status ? (
                          <span className="user__status-badge user__status-badge--active">Aktif</span>
                        ) : (
                          <span className="user__status-badge user__status-badge--blocked">Inactive</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            : null}
        </div>

        <div className="garage-detail__additional-setting">
          <h2 className="garage-detail__additional-header">Pengaturan Lainnya</h2>
          {additionalSettingToUpdate.map((item, idx) => (
            <div key={`additional-update-${idx}`}>
              <AdditionalGarageSetting
                serviceData={mappedRentTypes}
                locationId={mainSettingValue.location_id}
                data={item}
                setData={setAdditionalSettingToUpdate}
                numberInputOnWheelPreventChange={numberInputOnWheelPreventChange}
                handleKeyDown={handleKeyDown}
                index={idx}
              />
              <div className="garage-detail__divider">
                <Button
                  onClick={() => handleRemoveGarageSetting(idx, item.id)}
                  className="garage-detail__remove-button"
                  width={26}
                  height={26}
                >
                  <DeleteIcon />
                </Button>
              </div>
            </div>
          ))}
          {additionalSettingValue.map((item, idx) => (
            <div key={`additional-add-${idx}`}>
              <AdditionalGarageSetting
                serviceData={mappedRentTypes}
                locationId={mainSettingValue.location_id}
                data={item}
                setData={setAdditionalSettingValue}
                numberInputOnWheelPreventChange={numberInputOnWheelPreventChange}
                handleKeyDown={handleKeyDown}
                index={idx}
              />
              {idx === additionalSettingValue.length - 1 ? (
                <div className="garage-detail__divider">
                  <Button onClick={handleAddGarageSetting} className="garage-detail__add-button" width={26} height={26}>
                    <PlusIcon />
                  </Button>
                </div>
              ) : (
                <div className="garage-detail__divider">
                  <Button
                    onClick={() => handleRemoveGarageSetting(idx, item.id)}
                    className="garage-detail__remove-button"
                    width={26}
                    height={26}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="garage-detail__buttons">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} width={253} className="garage-btn">
            Kembali
          </Button>
          {(checkPermission(offCanvasMenu, currentMenu, 'update') || Object.keys(garageDetail).length === 0) && (
            <Button
              type="button"
              onClick={handleAddGarage}
              width={253}
              className="garage-btn"
              style={{ marginLeft: '10px' }}
            >
              {Object.keys(garageDetail).length === 0 ? 'Tambah' : 'Simpan'}
            </Button>
          )}
        </div>
      </TableWrapper>
    </div>
  );
};

export default GarageDetailComponent;
